import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, ButtonGroup, Button, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import AppDatePicker from "../../common/app-date-picker";
import ButtonThree from "../../common/third-button";
import TableSearchInput from "../TableSearchInput";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import {
  PHARMACY_PERFORMANCE_REPORT_ENDPOINT,
  PHARMACY_PERFORMANCE_REPORT_TREND_ENDPOINT,
} from "../../config/urls";

const TYPE_FILTER_OPTIONS = [
  { label: "All types", value: "" },
  { label: "General", value: "false" },
  { label: "Modern trade", value: "true" },
];

const ROLE_COLUMNS = [
  { key: "owner", label: "Owner" },
  { key: "supervisor", label: "Supervisor" },
  { key: "sales_person", label: "Sales Person" },
  { key: "other", label: "Other" },
];

const MEDAL_COLORS = ["#d4af37", "#a8a9ad", "#cd7f32"];

const LEADERBOARD_CSV_COLUMNS = [
  { id: "employee_id", label: "Employee ID" },
  { id: "employee_name", label: "Employee Name" },
  { id: "type", label: "Type" },
  { id: "total", label: "Total" },
  ...ROLE_COLUMNS.map((r) => ({ id: r.key, label: r.label })),
];

function downloadCsv(rows, filename) {
  if (!rows.length) return;
  const flat = rows.map((r) => ({ ...r, ...r.role_breakdown }));
  const header = LEADERBOARD_CSV_COLUMNS.map((c) => c.label).join(",");
  const lines = flat.map((r) =>
    LEADERBOARD_CSV_COLUMNS.map((c) => `"${String(r[c.id] ?? "").replace(/"/g, '""')}"`).join(",")
  );
  const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Sparkline({ data }) {
  const points = (data || []).map((v, i) => ({ i, v }));
  return (
    <Box sx={{ width: 110, height: 32 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points}>
          <Line type="monotone" dataKey="v" stroke={ColorsConstants.primaryBlue100 || "#2563eb"} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

function RankBadge({ index }) {
  if (index > 2) {
    return <Typography sx={{ ...typography.bodyMedium }}>{index + 1}</Typography>;
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <EmojiEventsIcon sx={{ color: MEDAL_COLORS[index], fontSize: 20 }} />
      <Typography sx={{ ...typography.bodyMedium, fontWeight: 700 }}>{index + 1}</Typography>
    </Box>
  );
}

function EmployeeTrendChart({ employeeId, authHeaders }) {
  const [granularity, setGranularity] = useState("daily");
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${PHARMACY_PERFORMANCE_REPORT_TREND_ENDPOINT(employeeId)}?granularity=${granularity}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch trend");
      const data = await res.json();
      setTrend(data.map((d) => ({ period: granularity === "weekly" ? d.label : dayjs(d.period).format("DD MMM"), count: d.count })));
    } catch (err) {
      console.error("Error fetching employee trend:", err);
      setTrend([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, granularity, authHeaders]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  return (
    <Box sx={{ p: 2, backgroundColor: ColorsConstants.primaryGray5 || "#f8fafc" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ ...typography.bodyMedium, fontWeight: 600 }}>Capture Trend</Typography>
        <ButtonGroup size="small" variant="outlined">
          <Button variant={granularity === "daily" ? "contained" : "outlined"} onClick={() => setGranularity("daily")}>Daily</Button>
          <Button variant={granularity === "weekly" ? "contained" : "outlined"} onClick={() => setGranularity("weekly")}>Weekly</Button>
        </ButtonGroup>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
      ) : trend.length === 0 ? (
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>No captures in this range.</Typography>
      ) : (
        <Box sx={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={ColorsConstants.primaryBlue100 || "#2563eb"} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

export default function PerformanceReportPanel() {
  const { getValidToken } = useAuth();
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Client-side pagination: the leaderboard API returns the full ranked list in one response
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const authHeaders = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant };
  }, [getValidToken]);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setPage(0); // filters/search changed -> back to the first page, like the other tabs
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dayjs(dateFrom).format("YYYY-MM-DD"));
    if (dateTo) params.set("date_to", dayjs(dateTo).format("YYYY-MM-DD"));
    if (typeFilter) params.set("mt_flag", typeFilter);
    if (search.trim()) params.set("search", search.trim());

    try {
      const headers = await authHeaders();
      const res = await fetch(`${PHARMACY_PERFORMANCE_REPORT_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch performance report");
      setRows(await res.json());
    } catch (err) {
      console.error("Error fetching performance report:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, typeFilter, search, authHeaders]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const hasFilters = Boolean(search || dateFrom || dateTo || typeFilter);
  const handleReset = () => {
    setSearch("");
    setDateFrom(null);
    setDateTo(null);
    setTypeFilter("");
    setPage(0);
  };

  const pageOffset = page * rowsPerPage;
  const pagedRows = rows.slice(pageOffset, pageOffset + rowsPerPage);

  const columns = [
    { id: "rank", label: "Rank", render: (_r, index) => <RankBadge index={pageOffset + index} /> },
    {
      id: "employee", label: "Employee",
      render: (r) => (
        <Box>
          <Typography sx={{ ...typography.bodyMedium }}>{r.employee_name}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>{r.employee_id}</Typography>
        </Box>
      ),
    },
    { id: "type", label: "Type", render: (r) => <AppChip label={r.type} color={r.type === "MT" ? "info" : "default"} variant="outlined" /> },
    { id: "total", label: "Total", render: (r) => <Typography sx={{ ...typography.bodyMedium, fontWeight: 700 }}>{r.total}</Typography> },
    ...ROLE_COLUMNS.map((rc) => ({ id: rc.key, label: rc.label, render: (r) => r.role_breakdown?.[rc.key] ?? 0 })),
    { id: "sparkline", label: "Last 14 Days", render: (r) => <Sparkline data={r.sparkline} /> },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Box sx={{ flex: 1, minWidth: 260, maxWidth: 400 }}>
          <TableSearchInput
            clearable
            searchQuery={search}
            onSearchChange={(e) => setSearch(e.target.value)}
            placeholder="Search Employee ID or Name"
          />
        </Box>
        <AppDatePicker label="From" value={dateFrom} onChange={setDateFrom} disableFuture fullWidth={false} />
        <AppDatePicker label="To" value={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} disableFuture fullWidth={false} />
        <AppDropdown
          menuItems={TYPE_FILTER_OPTIONS}
          selectedValue={typeFilter}
          onMenuSelect={(value) => setTypeFilter(value)}
        >
          {TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label || "All types"}
        </AppDropdown>
        <ButtonThree
          startIcon={<RestartAltIcon fontSize="small" />}
          onClick={handleReset}
          disabled={!hasFilters}
        >
          Reset
        </ButtonThree>
        <ButtonThree
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={() => downloadCsv(rows, "otm_performance_report.csv")}
          disabled={rows.length === 0}
        >
          Export
        </ButtonThree>
      </Box>

      <AppTable
        columns={columns}
        rows={pagedRows}
        rowKey="employee_id"
        loading={loading}
        emptyMessage="No performance data found"
        renderExpanded={(r) => <EmployeeTrendChart employeeId={r.employee_id} authHeaders={authHeaders} />}
        rowSx={(_r, index) => (pageOffset + index < 3 ? { backgroundColor: "rgba(212, 175, 55, 0.06)" } : {})}
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </LocalizationProvider>
  );
}
